"use client";

import { useTina, tinaField, coloredText } from "@/lib/utils";
import Section from "@/components/Section";
import Image from "next/image";
import Pill from "@/components/Pill";
import SeparatorBanner from "@/components/SeparatorBanner";
import CallToAction from "@/components/CallToAction";
import { CircleCheck } from "lucide-react";
import TextTooltip from "@/components/TextTooltip";

export default function CmmcCompliancePage(props) {
  const { data } = useTina({
    query: props.query,
    variables: props.variables,
    data: props.data,
  });

  const { header, separator, security, roadmap, callToAction } =
    data.cmmcCompliance;

  return (
    <div>
      <Header header={header} />
      <SeparatorBanner separator={separator} />
      <Section
        wrapperClassName="bg-gradient-to-t from-background-purplish to-background-blackish !pb-24"
        className="flex flex-col gap-12 lg:gap-24"
      >
        <Security security={security} />
        <Roadmap roadmap={roadmap} />
      </Section>
      <CallToAction callToAction={callToAction} />
    </div>
  );
}

const Header = ({ header }) => {
  const title = coloredText(header?.title);
  return (
    <Section
      as="header"
      wrapperClassName="!py-0"
      className="grid grid-cols-1 lg:grid-cols-2 items-center justify-center lg:gap-16 xl:min-h-[490px] justify-items-center"
    >
      <div className="flex flex-col gap-4 py-12">
        <h1
          data-tina-field={tinaField(header, "title")}
          dangerouslySetInnerHTML={{ __html: title }}
        />
        <TextTooltip
          text={header?.description}
          className="p-xl text-foreground-300"
          data-tina-field={tinaField(header, "description")}
        />
      </div>

      <Image
        src={header?.image ?? null}
        alt={header?.title}
        width={640}
        height={520}
        quality={100}
        data-tina-field={tinaField(header, "image")}
        className="aspect-[4/3.25] object-cover object-left self-center"
      />
    </Section>
  );
};

const Security = ({ security }) => {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 lg:pt-24 pb-12 items-start">
      <div className="flex flex-col gap-4 items-center lg:items-start">
        <Pill data-tina-field={tinaField(security, "sectionTitle")}>
          {security?.sectionTitle}
        </Pill>
        <h2
          className="whitespace-nowrap text-center lg:text-left"
          data-tina-field={tinaField(security, "title")}
        >
          {security?.title}
        </h2>
        <TextTooltip
          text={security?.description}
          className="p-xl text-foreground-300 text-center lg:text-left"
          data-tina-field={tinaField(security, "description")}
        />
      </div>
      <div className="flex flex-col items-center lg:items-start gap-6">
        {security?.benefits?.map((benefit, index) => (
          <div key={index}>
            <div className="flex items-center gap-3">
              <CircleCheck className="text-black size-8 fill-primary-300 flex-shrink-0" />
              <h3 className="h5">{benefit?.title}</h3>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const Roadmap = ({ roadmap }) => {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-16 items-center">
      <div className="bg-background-blackish rounded-lg overflow-hidden max-md:order-2">
        <Image
          data-tina-field={tinaField(roadmap, "image")}
          src={roadmap?.image ?? null}
          alt={roadmap?.title}
          width={500}
          height={500}
          quality={100}
          className="w-full h-full object-cover object-left-top aspect-[4/3] scale-100 origin-top-left translate-y-8 p-8 rounded-md"
        />
      </div>
      <div className="flex flex-col gap-6 max-md:order-1 max-md:items-center">
        <Pill data-tina-field={tinaField(roadmap, "sectionTitle")}>
          {roadmap?.sectionTitle}
        </Pill>
        <h2
          className="text-balance text-center md:text-left"
          data-tina-field={tinaField(roadmap, "title")}
        >
          {roadmap?.title}
        </h2>
        <TextTooltip
          text={roadmap?.description}
          className="p-xl text-foreground-300 text-center md:text-left"
          data-tina-field={tinaField(roadmap, "description")}
        />
      </div>
    </div>
  );
};
